import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Upload, Trash2, Loader2 } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Textarea } from 'components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';

import DatePicker from 'components/generic/DatePicker';
import LocationPicker from "components/generic/LocationPicker";
import PageTitle from 'components/generic/PageTitle';
import SuccessfulSubmission from 'components/generic/SuccessfulSubmission';

import { useEquipment } from 'context/EquipmentContext';
import { compressImage, compressPDF } from 'lib/utils';
// IMPORT the new APIs
import { getEquipmentById, updateEquipment } from 'services/equipmentService';
import { toast } from 'react-hot-toast';

const specNameToKey = (name = '') =>
  name.trim().replace(/\s+/g, '_').toLowerCase();

const AddEquipment = ({ onBack, equipmentId = null }) => {
  const navigate = useNavigate();
  const { createEquipment, loading: contextLoading } = useEquipment();

  // Local loading state for fetching data in edit mode
  const [fetchingData, setFetchingData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = !!equipmentId;

  // ----------- FORM STATES -----------
  const [equipmentName, setEquipmentName] = useState("");
  const [category, setCategory] = useState("");
  const [manufacturer, setManufacturer] = useState("");
  const [modelNumber, setModelNumber] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [description, setDescription] = useState("");
  const [initialStatus, setInitialStatus] = useState(null);
  const [purchasePrice, setPurchasePrice] = useState("");
  const [dailyRate, setDailyRate] = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");
  const [lastMaintenance, setLastMaintenance] = useState("");
  const [nextMaintenance, setNextMaintenance] = useState("");
  const [storageLocation, setStorageLocation] = useState(null);
  const [quantity, setQuantity] = useState("");
  const [processingStates, setProcessingStates] = useState({
    photos: false,
    manual: false,
    warranty: false,
  });

  const STATUS_OPTIONS = [
    { value: 1, label: "Available" },
    { value: 2, label: "Maintenance" },
  ];

  // Specs
  const [specs, setSpecs] = useState([
    { id: 1, name: '', value: '' },
    { id: 2, name: '', value: '' },
    { id: 3, name: '', value: '' },
    { id: 4, name: '', value: '' },
  ]);

  // Accessories
  const [accessories, setAccessories] = useState([]);
  const [newAccessory, setNewAccessory] = useState("");

  // ----------- FILE UPLOAD STATES -----------
  const [photos, setPhotos] = useState([]);
  const [manualFile, setManualFile] = useState(null);
  const [warrantyFile, setWarrantyFile] = useState(null);

  const [errors, setErrors] = useState({});
  const [isComplete, setIsComplete] = useState(false);

  const S3_BASE_URL = "https://beigexmemehouse.s3.amazonaws.com/beige/";

  // ----------- FETCH DATA FOR EDIT MODE -----------
 useEffect(() => {
  if (isEditMode) {
    const fetchDetails = async () => {
      setFetchingData(true);
      try {
        const res = await getEquipmentById(equipmentId);
        const data = res?.data?.data || res?.data || res;

        if (data) {
          // --- Basic Fields ---
          setEquipmentName(data.equipment_name || "");
          setCategory(data.category_id ? String(data.category_id) : "");
          setManufacturer(data.manufacturer || "");
          setModelNumber(data.model_number || "");
          setSerialNumber(data.serial_number || "");
          setDescription(data.description || "");
          setQuantity(data.quantity || "1");
          setPurchasePrice(data.purchase_price || "");
          setDailyRate(data.daily_rental_rate || "");
          setPurchaseDate(data.purchase_date || "");
          setLastMaintenance(data.last_maintenance_date || "");
          setNextMaintenance(data.next_maintenance_due || "");
          setStorageLocation(data.storage_location || null);

          if (data.initial_status_id) {
            const status = STATUS_OPTIONS.find(s => Number(s.value) === Number(data.initial_status_id));
            setInitialStatus(status || null);
          }

          // --- Map Technical Specs ---
          if (data.equipment_specs && Array.isArray(data.equipment_specs)) {
            setSpecs(data.equipment_specs.map((s) => ({
              id: s.spec_id,
              name: s.spec_name || "",
              value: s.spec_value || ""
            })));
          }

          // --- Map Accessories ---
          if (data.equipment_accessories && Array.isArray(data.equipment_accessories)) {
            setAccessories(data.equipment_accessories.map(a => a.accessory_name));
          }

          // --- Map Photos ---
          if (data.equipment_photos && Array.isArray(data.equipment_photos)) {
            const mappedPhotos = data.equipment_photos.map(p => ({
              name: p.file_url,
              size: "S3 File",
              url: `${S3_BASE_URL}${p.file_url}`,
              isExisting: true // Flag to know this is already on server
            }));
            setPhotos(mappedPhotos);
          }

          // --- Map Documents (Manual & Warranty) ---
          if (data.equipment_documents && Array.isArray(data.equipment_documents)) {
            const manual = data.equipment_documents.find(doc => doc.doc_type === "manual");
            const warranty = data.equipment_documents.find(doc => doc.doc_type === "warranty");

            if (manual) {
              setManualFile({
                name: manual.file_url,
                size: "S3 File",
                url: `${S3_BASE_URL}${manual.file_url}`,
                isExisting: true
              });
            }
            if (warranty) {
              setWarrantyFile({
                name: warranty.file_url,
                size: "S3 File",
                url: `${S3_BASE_URL}${warranty.file_url}`,
                isExisting: true
              });
            }
          }
        }
      } catch (error) {
        toast.error("Failed to load equipment details");
        console.error("Fetch Error:", error);
      } finally {
        setFetchingData(false);
      }
    };
    fetchDetails();
  }
}, [equipmentId, isEditMode]);
  // ----------- VALIDATION -----------
  const validateForm = () => {
    const newErrors = {};
    if (!equipmentName.trim()) newErrors.equipmentName = "Equipment Name is required";
    if (!category) newErrors.category = "Category is required";
    if (!modelNumber.trim()) newErrors.modelNumber = "Model Number is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ----------- UNIFIED SUBMISSION LOGIC -----------
  const handleSave = async (isDraft = 0) => {
  // 1. Validate if we are not saving a draft
  if (!isDraft && !validateForm()) return;

  setIsSubmitting(true);
  try {
    const fd = new FormData();
    const user = JSON.parse(localStorage.getItem('user')) || {};
    const organizationType = user?.organizationType;

    // --- BASIC TEXT/NUMBER FIELDS ---
    fd.append("equipment_name", equipmentName);
    fd.append("manufacturer", manufacturer);
    fd.append("model_number", modelNumber);
    fd.append("serial_number", serialNumber);
    fd.append("description", description);
    fd.append("category_id", category);
    fd.append("purchase_price", purchasePrice);
    fd.append("daily_rental_rate", dailyRate);
    fd.append("purchase_date", purchaseDate);
    fd.append("quantity", quantity);
    fd.append("last_maintenance_date", lastMaintenance);
    fd.append("next_maintenance_due", nextMaintenance);
    fd.append("is_draft", isDraft);

    // --- COMPLEX OBJECTS (JSON Strings) ---
    fd.append("storage_location_id", storageLocation ? JSON.stringify(storageLocation) : "");
    fd.append("initial_status_id", initialStatus?.value ?? "");

    if (organizationType === 2) {
      fd.append("organization_type", organizationType);
    }

    // --- SPECS & ACCESSORIES ---
    const formattedSpecs = specs
      .filter((s) => s.name?.trim())
      .map((s) => ({ name: s.name.trim(), value: s.value.trim() }));

    fd.append("specs", JSON.stringify(formattedSpecs));
    fd.append("accessories", JSON.stringify(accessories));

    // --- PHOTO LOGIC (Binary vs Existing IDs) ---
    const existingPhotoIds = [];
    photos.forEach((p) => {
      if (p.file) {
        // This is a NEWLY uploaded binary file
        fd.append("photos", p.file);
      } else if (p.isExisting && p.id) {
        // This is an OLD file from S3 we want to keep
        existingPhotoIds.push(p.id);
      }
    });
    // Send the array of IDs the backend should RETAIN
    fd.append("existing_photo_ids", JSON.stringify(existingPhotoIds));

    // --- MANUAL DOCUMENT LOGIC ---
    if (manualFile?.file) {
      fd.append("manual", manualFile.file); // New upload
    } else if (manualFile?.isExisting && manualFile.id) {
      fd.append("existing_manual_id", manualFile.id); // Retention ID
    }

    // --- WARRANTY DOCUMENT LOGIC ---
    if (warrantyFile?.file) {
      fd.append("warranty", warrantyFile.file); // New upload
    } else if (warrantyFile?.isExisting && warrantyFile.id) {
      fd.append("existing_warranty_id", warrantyFile.id); // Retention ID
    }

    // --- API CALL EXECUTION ---
    let res;
    if (isEditMode) {
      // For updates, ensure the equipment_id is also in the body
      fd.append("equipment_id", equipmentId);
      res = await updateEquipment(equipmentId, fd);
    } else {
      res = await createEquipment(fd);
    }

    // --- RESPONSE HANDLING ---
    // Check for success across different response structures
    const isSuccess = res.success || res.status === 200 || (res.data && !res.error);

    if (isSuccess) {
      if (isDraft) {
        toast.success("Draft saved successfully");
        onBack(); // Return to list
      } else {
        setIsComplete(true); // Show SuccessfulSubmission component
      }
    } else {
      const errorMsg = res.error || res.message || "Something went wrong";
      toast.error(errorMsg);
    }
  } catch (err) {
    console.error("❌ SUBMIT ERROR:", err);
    toast.error("An unexpected error occurred while saving.");
  } finally {
    setIsSubmitting(false);
  }
};

  // Helper to update value and clear error
  const handleFieldChange = (field, value, setter) => {
    setter(value);
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleUpload = async (e, setter, isArray = false, fieldKey) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;

    try {
      setProcessingStates(prev => ({ ...prev, [fieldKey]: true }));
      let processedFile = file;

      if (file.type.startsWith('image/')) {
        processedFile = await compressImage(file);
      } else if (file.type === 'application/pdf') {
        processedFile = await compressPDF(file);
      }

      const fileData = {
        name: processedFile.name,
        size: (processedFile.size / 1024 / 1024).toFixed(1) + " MB",
        file: processedFile,
        url: URL.createObjectURL(processedFile),
      };

      if (isArray) {
        setter((prev) => [...prev, fileData].slice(0, 2));
      } else {
        setter(fileData);
      }
    } catch (error) {
      console.error("Compression failed:", error);
    } finally {
      setProcessingStates(prev => ({ ...prev, [fieldKey]: false }));
    }
  };

  const addSpec = () => {
    setSpecs([...specs, { id: Date.now(), name: "", value: "" }]);
  };

  const handleSpecChange = (id, newName) => {
    setSpecs(specs.map(spec =>
      spec.id === id ? {
        ...spec,
        name: newName,
        value: specNameToKey(newName)
      } : spec
    ));
  };

  const addAccessory = () => {
    if (!newAccessory.trim()) return;
    setAccessories([...accessories, newAccessory]);
    setNewAccessory("");
  };

  const removeAccessory = (item) => {
    setAccessories(accessories.filter(a => a !== item));
  };

  if (fetchingData) {
    return (
        <div className="flex h-96 items-center justify-center">
            <Loader2 className="animate-spin text-primary" size={40} />
            <span className="ml-3 text-lg">Loading equipment data...</span>
        </div>
    );
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      {isComplete ? (
        <SuccessfulSubmission
          title={isEditMode ? "Equipment Updated" : "Equipment Created"}
          description={isEditMode ? "The equipment details have been updated." : "The equipment has been successfully added."}
          buttonText="View All Equipments"
          onConfirm={() => navigate("/equipment")}
          onClose={() => navigate(-1)}
        />
      ) : (
        <>
          <Button
            variant="ghost"
            className="mb-6 pl-0 hover:pl-2 transition-all gap-2 hover:bg-transparent"
            onClick={onBack}
          >
            <ArrowLeft size={20} />
            Back to Equipment Management
          </Button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              <PageTitle
                title={isEditMode ? "Edit Equipment" : "Add New Equipment"}
                description={isEditMode ? "Update the details of your inventory item" : "Add equipment to your inventory"}
              />

              <div className="border border-border bg-card rounded-2xl items-start p-6">
                <h2 className="text-lg font-semibold mb-6">Equipment Information</h2>
                <div className="space-y-6">
                  {/* EQUIPMENT NAME */}
                  <div>
                    <label className="text-sm font-medium">
                      Equipment Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={equipmentName}
                      onChange={e => handleFieldChange("equipmentName", e.target.value, setEquipmentName)}
                      placeholder={"eg. Sony FX6 Cinema Camera"}
                      className={errors.equipmentName ? 'border-red-500' : ''}
                    />
                    {errors.equipmentName && <p className="text-red-500 text-xs mt-1">{errors.equipmentName}</p>}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* CATEGORY */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Category <span className="text-red-500">*</span>
                      </label>
                      <Select
                        value={category}
                        onValueChange={(v) => handleFieldChange("category", v, setCategory)}
                      >
                        <SelectTrigger className={errors.category ? 'border-red-500' : ''}>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="1">Camera</SelectItem>
                          <SelectItem value="2">Lighting</SelectItem>
                          <SelectItem value="3">Audio</SelectItem>
                        </SelectContent>
                      </Select>
                      {errors.category && <p className="text-red-500 text-xs mt-1">{errors.category}</p>}
                    </div>

                    <div>
                      <label className="text-sm font-medium">Manufacturer</label>
                      <Input className="h-12 mt-2" value={manufacturer} onChange={e => setManufacturer(e.target.value)} placeholder={"Sony"} />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* MODEL NUMBER */}
                    <div>
                      <label className="text-sm font-medium">
                        Model Number <span className="text-red-500">*</span>
                      </label>
                      <Input
                        value={modelNumber}
                        onChange={e => handleFieldChange("modelNumber", e.target.value, setModelNumber)}
                        placeholder={"eg: SN-CAM-001"}
                        className={errors.modelNumber ? 'border-red-500' : ''}
                      />
                      {errors.modelNumber && <p className="text-red-500 text-xs mt-1">{errors.modelNumber}</p>}
                    </div>

                    <div>
                      <label className="text-sm font-medium">Serial Number</label>
                      <Input value={serialNumber} onChange={e => setSerialNumber(e.target.value)} placeholder={"eg: N-CAM-001"} />
                    </div>
                  </div>
                    <div>
                      <label className="text-sm font-medium">Quantity</label>
                      <Input
                        type="number"
                        min="1"
                        value={quantity}
                        onChange={e => {
                          const val = Math.max(1, Number(e.target.value));
                          handleFieldChange("quantity", val, setQuantity);
                        }}
                        placeholder="Enter quantity"
                        className={errors.quantity ? 'border-red-500' : ''}
                      />
                    </div>
                  <div>
                    <label className="text-sm font-medium">Description</label>
                    <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder={"Description of the equipment...."} />
                  </div>
                </div>
              </div>

              {/* Pricing, Specs, Accessories sections remain largely the same... */}
              {/* Pricing */}
              <div className="border border-border bg-card rounded-2xl items-start p-6">
                <h2 className="text-lg font-semibold mb-6">Pricing</h2>
                <div className="space-y-6">
                  <div>
                    <label className="text-sm font-medium">Purchase Price</label>
                    <Input type="number" min={1} value={purchasePrice} onChange={e => setPurchasePrice(e.target.value)} placeholder={"00.00"} />
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <label className="text-sm font-medium">Daily Rental Rate</label>
                      <Input type="number" value={dailyRate} onChange={e => setDailyRate(e.target.value)} placeholder={"00.00"} />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Purchase Date</label>
                      <DatePicker value={purchaseDate} onChange={setPurchaseDate} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Technical Specs */}
              <div className="border border-border bg-card rounded-2xl items-start p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-semibold">Technical Specifications</h2>
                  <Button size="sm" className="bg-button-coloured rounded-lg text-button-coloured-foreground gap-1" onClick={addSpec}>
                    <Plus size={16} /> Add more
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {specs.map((spec) => (
                    <Input
                      key={spec.id}
                      placeholder="Spec name (e.g., Resolution)"
                      value={spec.name}
                      onChange={(e) => handleSpecChange(spec.id, e.target.value)}
                    />
                  ))}
                </div>
              </div>

              {/* Accessories */}
              <div className="border border-border bg-card rounded-2xl items-start p-6">
                <h2 className="text-lg font-semibold mb-6">Accessories</h2>
                <div className="flex gap-3">
                  <Input
                    value={newAccessory}
                    onChange={(e) => setNewAccessory(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addAccessory()}
                    placeholder="e.g., Battery Kit"
                  />
                  <Button onClick={addAccessory}>+ Add</Button>
                </div>
                <div className="mt-4 space-y-2">
                  {accessories.map((a) => (
                    <div key={a} className="flex justify-between p-2 border rounded-lg bg-gray-50">
                      {a}
                      <Trash2 size={16} className="text-red-500 cursor-pointer" onClick={() => removeAccessory(a)} />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* RIGHT SIDEBAR */}
            <div className="space-y-6">
              <div className="relative bg-card rounded-2xl border border-border p-6 w-full">
                <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
                <Button
                  className="w-full mb-3"
                  onClick={() => handleSave(0)}
                  disabled={isSubmitting || contextLoading}
                >
                  {isSubmitting ? <Loader2 className="animate-spin mr-2" size={18} /> : <Plus size={18} />}
                  {isEditMode ? "Update Equipment" : "Add Equipment"}
                </Button>
                <Button
                  className="w-full"
                  variant="outline"
                  onClick={() => handleSave(1)}
                  disabled={isSubmitting || contextLoading}
                >
                  <Upload size={18} className="rotate-180 mr-2" />
                  Save Draft
                </Button>
              </div>

              {/* Location & Status, Maintenance, Files sections remain similar... */}
              <div className="border border-border bg-card rounded-2xl items-start p-6">
                <h2 className="text-sm font-semibold mb-4">Location & Status</h2>
                <div className="mb-4">
                  <label className="text-xs font-medium">Storage Location</label>
                  <LocationPicker
                    value={storageLocation}
                    onChange={setStorageLocation}
                    placeholder="Click to select venue location on map"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium">Initial Status</label>
                  <Select
                    value={initialStatus?.value ? String(initialStatus.value) : undefined}
                    onValueChange={(v) =>
                      setInitialStatus(STATUS_OPTIONS.find(s => String(s.value) === v))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map(s => (
                        <SelectItem key={s.value} value={String(s.value)}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="border border-border bg-card rounded-2xl items-start p-6">
                <h2 className="text-sm font-semibold mb-4">Maintenance</h2>
                <div className="mb-4">
                  <label className="text-xs font-medium">Last Maintenance</label>
                  <DatePicker value={lastMaintenance} onChange={setLastMaintenance} />
                </div>
                <div>
                  <label className="text-xs font-medium">Next Maintenance Due</label>
                  <DatePicker value={nextMaintenance} onChange={setNextMaintenance} />
                </div>
              </div>

              <div className="border border-border bg-card rounded-2xl items-start p-6">
                <h2 className="text-base font-semibold mb-4">Photos & Documents</h2>
                {/* Upload logic stays the same */}
                {/* ... existing upload JSX ... */}
                {photos.length < 2 ? (
                  <label className={`w-full border border-dashed border-gray-300 rounded-xl p-8 flex flex-col items-center justify-center mb-4 ${processingStates.photos ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-gray-50"}`}>
                    {processingStates.photos ? (
                      <>
                        <Loader2 className="animate-spin" size={20} />
                        <p className="text-sm mt-2 font-medium">Processing file...</p>
                      </>
                    ) : (
                      <>
                        <Plus size={20} />
                        <p className="text-sm mt-2">Upload photo, pdf etc</p>
                        <p className="text-xs text-gray-400">Choose file to upload</p>
                      </>
                    )}
                    <input type="file" className="hidden" disabled={processingStates.photos} onChange={(e) => handleUpload(e, setPhotos, true, "photos")} />
                  </label>
                ) : null}
                {photos.map((p, index) => (
                  <div key={index} className="w-full flex justify-between items-center bg-gray-50 border rounded-lg px-4 py-2 mb-2">
                    <div>
                      <p className="text-sm font-medium">{p.name}</p>
                      <p className="text-xs text-gray-500">{p.size}</p>
                    </div>
                    <button className="text-red-500 text-xs" onClick={() => setPhotos(prev => prev.filter((_, i) => i !== index))}><Trash2 size={16} /></button>
                  </div>
                ))}

                {/* Manual File Preview */}
                {manualFile ? (
                  <div className="flex justify-between items-center bg-gray-50 border rounded-lg px-4 py-2 mt-2">
                    <div>
                      <p className="text-sm font-medium">{manualFile.name}</p>
                      <p className="text-xs text-gray-500">{manualFile.size}</p>
                    </div>
                    <button className="text-red-500 text-xs" onClick={() => setManualFile(null)}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                ) : (
                  <label className={`flex items-center justify-between border rounded-lg px-4 py-3 mt-4 ${processingStates.manual ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-gray-50"}`}>
                    <div className="flex items-center gap-2">
                      {processingStates.manual ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}
                      <span className="text-sm">{processingStates.manual ? "Processing..." : "Upload Manual"}</span>
                    </div>

                    <input
                      type="file"
                      accept="image/png,image/jpeg,application/pdf,"
                      className="hidden"
                      disabled={processingStates.manual}
                      onChange={(e) => handleUpload(e, setManualFile, false, "manual")}
                    />
                  </label>
                )}

                {/* Warranty File Preview */}
                {warrantyFile ? (
                  <div className="flex justify-between items-center bg-gray-50 border rounded-lg px-4 py-2 mt-2">
                    <div>
                      <p className="text-sm font-medium">{warrantyFile.name}</p>
                      <p className="text-xs text-gray-500">{warrantyFile.size}</p>
                    </div>
                    <button className="text-red-500 text-xs" onClick={() => setWarrantyFile(null)}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                ) : (
                  <label className={`flex items-center justify-between border rounded-lg px-4 py-3 mt-4 ${processingStates.warranty ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-gray-50"}`}>
                    <div className="flex items-center gap-2">
                      {processingStates.warranty ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}
                      <span className="text-sm">{processingStates.manwarrantyual ? "Processing..." : "Upload Warranty"}</span>
                    </div>

                    <input
                      type="file"
                      accept="image/png,image/jpeg,application/pdf,"
                      className="hidden"
                      disabled={processingStates.warranty}
                      onChange={(e) => handleUpload(e, setWarrantyFile, false, "warranty")}
                    />
                  </label>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AddEquipment;
